import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { StandardAppLayout } from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Loader from 'src/shared/components/loader/Loader';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import { useAppDispatch } from 'src/store';
import { useListEngineApiTokensQuery, type ApiTokenSummary } from './state/api/engineApiTokensEngineSlice';
import { openCreate, openDetail, openRevoke } from './state/engineApiTokensSlice';
import TokenListTable from './components/token-list/TokenListTable';
import ShowRevokedToggle from './components/show-revoked-toggle/ShowRevokedToggle';
import EngineApiTokensModals from './components/EngineApiTokensModals';

const EngineApiTokens = () => {
    const dispatch = useAppDispatch();
    const [includeRevoked, setIncludeRevoked] = useState(false);

    const { data, isLoading, error, refetch } = useListEngineApiTokensQuery({ includeRevoked });

    const tokens: ApiTokenSummary[] = data?.tokens ?? [];

    const handleRowClick = (token: ApiTokenSummary) => dispatch(openDetail(token));
    const handleRevoke = (token: ApiTokenSummary) => dispatch(openRevoke(token));
    const handleCreate = () => dispatch(openCreate());

    const mainContent = (() => {
        if (isLoading) {
            return (
                <div className='flex justify-center py-24' data-testid='engine-api-tokens-loading'>
                    <Loader />
                </div>
            );
        }

        if (error) {
            return (
                <div className='flex flex-col items-center justify-center py-24 gap-4 text-center' data-testid='engine-api-tokens-error'>
                    <div className='flex items-center justify-center w-16 h-16 rounded-full bg-red-0'>
                        <AlertCircle className='w-8 h-8 text-red-100' />
                    </div>
                    <div className='flex flex-col items-center gap-1 max-w-sm'>
                        <h3 className='text-lg font-semibold text-primary'>Unable to load tokens</h3>
                        <p className='text-sm text-grey-70'>The engine returned an error. Try again, or check your connection.</p>
                    </div>
                    <SecondaryButton onClick={() => refetch()} data-testid='engine-api-tokens-retry'>
                        Retry
                    </SecondaryButton>
                </div>
            );
        }

        return <TokenListTable tokens={tokens} includeRevoked={includeRevoked} onRowClick={handleRowClick} onRevoke={handleRevoke} />;
    })();

    return (
        <>
            <StandardAppLayout
                title='Engine API Tokens'
                otherActions={
                    <div className='flex items-center gap-4'>
                        <ShowRevokedToggle checked={includeRevoked} onChange={setIncludeRevoked} />
                        <PrimaryButton onClick={handleCreate} data-testid='engine-api-tokens-new'>
                            New token
                        </PrimaryButton>
                    </div>
                }
                mainContent={mainContent}
            />
            <EngineApiTokensModals />
        </>
    );
};

export default EngineApiTokens;
